import { TourConfig, startTour, CONTINUE_PLATFORM_TOUR_BUTTON } from './TourSystem';
import PageWelcomeModal from './PageWelcomeModal';
import 'driver.js/dist/driver.css';

// PHASE 1: Chat Tab (default)
const unityBotPhase1Config: TourConfig = {
    tourId: 'unitybot-phase1',
    steps: [
        {
            popover: {
                title: '🤖 Welcome to UnityBot',
                description: 'Your AI-powered assistant with five specialized modes: Chat, SNN Simulator, Time Forecast, Neural Visualization, and History. Each mode uses advanced AI for different business tasks. This tour covers all five tabs in detail.',
            }
        },
        {
            element: '#unitybot-tabs-container',
            popover: {
                title: '🎛️ Five AI Capability Modes',
                description: 'UnityBot has five tabs representing different AI models: (1) Chat - conversational AI assistant, (2) SNN Simulator - neural network simulations, (3) Time Forecast - predictive analytics, (4) Neural Visualization - data visualization, (5) History - past interactions. Let me show each mode.',
                side: "bottom",
            }
        },
        {
            element: '#unitybot-tabs-container > div > button:nth-child(1)',
            popover: {
                title: '💬 Chat Mode (Current View)',
                description: 'Chat is a GPT-powered conversational AI that answers questions, generates content, helps with code, provides business advice, and assists with research. Type any question or request and get instant intelligent responses. Supports file uploads and multi-turn conversations.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '💡 Chat Capabilities',
                description: 'Chat mode can: write code, debug errors, explain concepts, generate marketing copy, analyze documents, translate languages, summarize articles, create outlines, brainstorm ideas, and provide step-by-step instructions. It is context-aware across your entire conversation.',
            }
        },
        {
            popover: {
                title: '🔄 Switching to SNN Simulator',
                description: 'Now I will show you the SNN (Spiking Neural Network) Simulator for advanced neural network modeling and simulation. Switching in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const snnTab = document.querySelector('#unitybot-tabs-container > div > button:nth-child(2)');
            if (snnTab) {
                (snnTab as HTMLElement).click();
                setTimeout(() => {
                    startUnityBotPhase2Tour();
                }, 800);
            }
        }, 1000);
    }
};

// PHASE 2: SNN Tab
const unityBotPhase2Config: TourConfig = {
    tourId: 'unitybot-phase2',
    steps: [
        {
            element: '#unitybot-tabs-container > div > button:nth-child(2)',
            popover: {
                title: '🧠 SNN Simulator Mode',
                description: 'Spiking Neural Network Simulator lets you model brain-like neural networks for advanced AI research. Design network architectures, configure neuron parameters, run simulations, and visualize neural activity patterns. Used for: robotics control, pattern recognition, and biological neural modeling.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '⚙️ SNN Features',
                description: 'You can: (1) Create custom neural network topologies, (2) Set firing thresholds and synaptic weights, (3) Run time-based simulations with spike visualization, (4) Export trained models for deployment, (5) Import existing network configurations, (6) Analyze network performance metrics.',
            }
        },
        {
            popover: {
                title: '🔄 Switching to Time Forecast',
                description: 'Next, I will demonstrate the Time Forecast mode for predictive analytics and time-series forecasting. Switching in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const forecastTab = document.querySelector('#unitybot-tabs-container > div > button:nth-child(3)');
            if (forecastTab) {
                (forecastTab as HTMLElement).click();
                setTimeout(() => {
                    startUnityBotPhase3Tour();
                }, 800);
            }
        }, 1000);
    }
};

// PHASE 3: Forecast Tab
const unityBotPhase3Config: TourConfig = {
    tourId: 'unitybot-phase3',
    steps: [
        {
            element: '#unitybot-tabs-container > div > button:nth-child(3)',
            popover: {
                title: '📈 Time Forecast Mode',
                description: 'Time Forecast uses AI to predict future trends based on historical data. Upload time-series datasets (sales, stock prices, user growth, etc.) and get: future predictions, confidence intervals, trend analysis, seasonality detection, and anomaly identification. Perfect for business planning.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '📊 Forecasting Capabilities',
                description: 'Features include: (1) Multiple forecasting algorithms (ARIMA, Prophet, LSTM), (2) Automatic parameter tuning, (3) Interactive prediction charts, (4) Export forecasts as CSV/Excel, (5) Scenario analysis ("what-if" modeling), (6) Accuracy metrics and validation.',
            }
        },
        {
            popover: {
                title: '🔄 Switching to Neural Visualization',
                description: 'Now I will show you the Neural Visualization mode for creating beautiful data visualizations using AI. Switching in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const vizTab = document.querySelector('#unitybot-tabs-container > div > button:nth-child(4)');
            if (vizTab) {
                (vizTab as HTMLElement).click();
                setTimeout(() => {
                    startUnityBotPhase4Tour();
                }, 800);
            }
        }, 1000);
    }
};

// PHASE 4: Neural Viz Tab
const unityBotPhase4Config: TourConfig = {
    tourId: 'unitybot-phase4',
    steps: [
        {
            element: '#unitybot-tabs-container > div > button:nth-child(4)',
            popover: {
                title: '🔮 Neural Visualization Mode',
                description: 'Neural Visualization turns raw data into stunning interactive charts using AI. Upload datasets and automatically generate: 3D scatter plots, heatmaps, network graphs, decision trees, correlation matrices, and custom visualizations. The AI suggests the best visualization type for your data.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🎨 Visualization Tools',
                description: 'You can: (1) Auto-generate charts from CSV/Excel files, (2) Customize colors, labels, and axes, (3) Create interactive dashboards, (4) Export as PNG/SVG/PDF, (5) Embed visualizations in reports, (6) Animate time-based data changes.',
            }
        },
        {
            popover: {
                title: '🔄 Switching to History',
                description: 'Finally, let me show you the History tab where all your past AI interactions are saved and searchable. Switching in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const historyTab = document.querySelector('#unitybot-tabs-container > div > button:nth-child(5)');
            if (historyTab) {
                (historyTab as HTMLElement).click();
                setTimeout(() => {
                    startUnityBotPhase5Tour();
                }, 800);
            }
        }, 1000);
    }
};

// PHASE 5: History Tab
const unityBotPhase5Config: TourConfig = {
    tourId: 'unitybot-phase5',
    steps: [
        {
            element: '#unitybot-tabs-container > div > button:nth-child(5)',
            popover: {
                title: '📜 History & Archive Mode',
                description: 'History stores all your past interactions across ALL UnityBot modes. Search conversations, review forecasts, re-download visualizations, and revisit SNN simulations. Everything is timestamped, categorized, and searchable. Never lose your AI-generated work.',
                side: "bottom",
            }
        },
        {
            popover: {
                title: '🔍 History Features',
                description: 'You can: (1) Search history by keyword, date, or mode, (2) Filter by AI model (Chat/SNN/Forecast/Viz), (3) Restore previous conversations to continue them, (4) Export entire history as backup, (5) Delete individual entries or bulk clear, (6) Star favorite interactions for quick access.',
            }
        },
        {
            popover: {
                title: '💡 UnityBot Pro Tips',
                description: '(1) Use Chat for general AI assistance and quick answers. (2) SNN Simulator is for advanced AI researchers and robotics. (3) Time Forecast helps with business planning and predictions. (4) Neural Viz makes data presentations impressive. (5) History ensures you never lose AI work. (6) All modes run on premium GPUs for fast results.',
            }
        },
        {
            popover: {
                title: '🚀 AI-Powered Productivity',
                description: `You now understand all five UnityBot capabilities. Each mode is a specialized AI tool designed for specific business and research tasks. Combine them for powerful workflows: chat for ideas → forecast for planning → visualize for presentations.${CONTINUE_PLATFORM_TOUR_BUTTON}`,
            }
        }
    ],
};

export const startUnityBotTour = () => {
    startTour(unityBotPhase1Config);
};

const startUnityBotPhase2Tour = () => {
    startTour(unityBotPhase2Config);
};

const startUnityBotPhase3Tour = () => {
    startTour(unityBotPhase3Config);
};

const startUnityBotPhase4Tour = () => {
    startTour(unityBotPhase4Config);
};

const startUnityBotPhase5Tour = () => {
    startTour(unityBotPhase5Config);
};

const UnityBotTour = () => {
    return (
        <PageWelcomeModal
            title="Welcome to UnityBot"
            description="Your suite of five AI-powered tools for business, research, and productivity."
            onStartTour={startUnityBotTour}
            tourId="unitybot-phase1"
        />
    );
};

export default UnityBotTour;
